import {
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js';
import { motion } from 'framer-motion';
import {
  BarChart3,
  Calculator,
  FileText,
  Home,
  IndianRupee,
  Percent,
  TrendingUp,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';

import { ToolLayout } from '@layouts/ToolLayout';

import { LocalizedCurrency } from '@ui/LocalizedNumber';
import NumberInputWithSlider from '@ui/NumberInputWithSlider';
import RadioGroup from '@ui/RadioGroup';
import { ResultSummaryCard } from '@ui/ResultSummaryCard';
import Toggle from '@ui/Toggle';
import { WhatsAppCTA } from '@ui/WhatsAppCTA';

import { COLORS } from '@constants/colors';
import { CONTACT_INFO } from '@constants/contact';

import {
  formatCurrency,
  formatIndianNumberShort,
} from '@utils/numberFormatter';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

export const HomeLoanCalculator = () => {
  // Home Loan Calculator State
  const [loanAmount, setLoanAmount] = useState<number | null>(5000000); // 50 Lakhs
  const [interestRate, setInterestRate] = useState<number | null>(8.5);
  const [loanTenure, setLoanTenure] = useState<number | null>(20);
  const [tenureType, setTenureType] = useState<'years' | 'months'>('years');
  const [prepaymentEnabled, setPrepaymentEnabled] = useState<boolean>(false);
  const [prepaymentType, setPrepaymentType] = useState<'yearly' | 'monthly'>(
    'yearly'
  );
  const [prepaymentAmount, setPrepaymentAmount] = useState<number | null>(
    100000
  );
  const [prepaymentStartYear, setPrepaymentStartYear] = useState<number | null>(
    2
  );
  const [results, setResults] = useState({
    monthlyEMI: 0,
    totalInterest: 0,
    totalAmount: 0,
    interestSaved: 0,
    tenureReduction: 0,
  });

  // Calculate EMI and loan details
  const calculateHomeLoan = useCallback(() => {
    const principal = loanAmount || 0;
    const rate = interestRate || 0;
    const tenure = loanTenure || 0;
    const prepayment = prepaymentAmount || 0;
    const startYear = prepaymentStartYear || 1;

    if (principal <= 0 || rate <= 0 || tenure <= 0) {
      setResults({
        monthlyEMI: 0,
        totalInterest: 0,
        totalAmount: 0,
        interestSaved: 0,
        tenureReduction: 0,
      });
      return;
    }

    const monthlyRate = rate / 100 / 12;
    const totalMonths = tenureType === 'years' ? tenure * 12 : tenure;

    // Calculate EMI using formula: EMI = P * r * (1+r)^n / ((1+r)^n - 1)
    const emi =
      (principal * monthlyRate * Math.pow(1 + monthlyRate, totalMonths)) /
      (Math.pow(1 + monthlyRate, totalMonths) - 1);

    // Calculate without prepayment
    const totalAmountWithoutPrepayment = emi * totalMonths;
    const totalInterestWithoutPrepayment =
      totalAmountWithoutPrepayment - principal;

    let totalInterestWithPrepayment = totalInterestWithoutPrepayment;
    let actualTenure = totalMonths;
    let interestSaved = 0;
    let tenureReduction = 0;

    if (prepaymentEnabled && prepayment > 0) {
      // Calculate with prepayment
      let remainingPrincipal = principal;
      let totalInterestPaid = 0;
      let monthsPaid = 0;
      const startMonth = (startYear - 1) * 12;

      for (let month = 1; month <= totalMonths; month++) {
        if (remainingPrincipal <= 0) break;

        const interestComponent = remainingPrincipal * monthlyRate;
        const principalComponent = emi - interestComponent;

        totalInterestPaid += interestComponent;
        remainingPrincipal -= principalComponent;
        monthsPaid++;

        // Apply prepayment
        if (
          prepaymentType === 'yearly' &&
          month >= startYear * 12 &&
          month % 12 === 0
        ) {
          remainingPrincipal = Math.max(0, remainingPrincipal - prepayment);
        } else if (prepaymentType === 'monthly' && month >= startMonth) {
          remainingPrincipal = Math.max(0, remainingPrincipal - prepayment);
        }
      }

      totalInterestWithPrepayment = totalInterestPaid;
      actualTenure = monthsPaid;
      interestSaved =
        totalInterestWithoutPrepayment - totalInterestWithPrepayment;
      tenureReduction = totalMonths - actualTenure;
    }

    setResults({
      monthlyEMI: emi,
      totalInterest: totalInterestWithPrepayment,
      totalAmount: principal + totalInterestWithPrepayment,
      interestSaved: interestSaved,
      tenureReduction: tenureReduction,
    });
  }, [
    loanAmount,
    interestRate,
    loanTenure,
    tenureType,
    prepaymentEnabled,
    prepaymentType,
    prepaymentAmount,
    prepaymentStartYear,
  ]);

  useEffect(() => {
    calculateHomeLoan();
  }, [calculateHomeLoan]);

  // Generate chart data for loan breakdown - showing yearly principal vs interest components
  const generateChartData = () => {
    const principal = loanAmount || 0;
    const rate = interestRate || 0;
    const tenure = loanTenure || 0;
    const prepayment = prepaymentAmount || 0;
    const startYear = prepaymentStartYear || 1;

    if (principal <= 0 || rate <= 0 || tenure <= 0) {
      return { labels: [], datasets: [] };
    }

    const monthlyRate = rate / 100 / 12;
    const totalMonths = tenureType === 'years' ? tenure * 12 : tenure;
    const emi = results.monthlyEMI;

    const years = [];
    const yearlyPrincipalData = []; // Yearly principal component
    const yearlyInterestData = []; // Yearly interest component

    let remainingPrincipal = principal;
    const startMonth = (startYear - 1) * 12;

    for (let year = 1; year <= Math.ceil(totalMonths / 12); year++) {
      let yearlyInterest = 0;
      let yearlyPrincipal = 0;

      for (let month = 1; month <= 12; month++) {
        const currentMonth = (year - 1) * 12 + month;
        if (currentMonth > totalMonths || remainingPrincipal <= 0) break;

        const interestComponent = remainingPrincipal * monthlyRate;
        const principalComponent = emi - interestComponent;

        yearlyInterest += interestComponent;
        yearlyPrincipal += principalComponent;
        remainingPrincipal -= principalComponent;

        // Apply prepayment
        if (prepaymentEnabled && prepayment > 0) {
          if (
            prepaymentType === 'yearly' &&
            year >= startYear &&
            month === 12
          ) {
            remainingPrincipal = Math.max(0, remainingPrincipal - prepayment);
          } else if (
            prepaymentType === 'monthly' &&
            currentMonth >= startMonth
          ) {
            remainingPrincipal = Math.max(0, remainingPrincipal - prepayment);
          }
        }
      }

      years.push(`Year ${year}`);
      yearlyPrincipalData.push(yearlyPrincipal); // This year's principal component
      yearlyInterestData.push(yearlyInterest); // This year's interest component

      if (remainingPrincipal <= 0) break;
    }

    return {
      labels: years,
      datasets: [
        {
          label: 'Yearly principal component',
          data: yearlyPrincipalData,
          borderColor: COLORS.sage,
          backgroundColor: `${COLORS.sage}20`,
          fill: true,
          tension: 0.4,
          pointBackgroundColor: COLORS.sage,
          pointBorderColor: COLORS.sage,
          pointRadius: 4,
          pointHoverRadius: 6,
        },
        {
          label: 'Yearly interest component',
          data: yearlyInterestData,
          borderColor: COLORS.accent,
          backgroundColor: `${COLORS.accent}20`,
          fill: false,
          tension: 0.4,
          pointBackgroundColor: COLORS.accent,
          pointBorderColor: COLORS.accent,
          pointRadius: 4,
          pointHoverRadius: 6,
        },
      ],
    };
  };

  // Generate amortization table data
  const generateTableData = () => {
    const principal = loanAmount || 0;
    const rate = interestRate || 0;
    const tenure = loanTenure || 0;
    const prepayment = prepaymentAmount || 0;
    const startYear = prepaymentStartYear || 1;

    if (principal <= 0 || rate <= 0 || tenure <= 0) {
      return [];
    }

    const monthlyRate = rate / 100 / 12;
    const totalMonths = tenureType === 'years' ? tenure * 12 : tenure;
    const emi = results.monthlyEMI;

    const tableData = [];
    let remainingPrincipal = principal;
    let cumulativeInterest = 0;
    let cumulativePrincipal = 0;
    const startMonth = (startYear - 1) * 12;

    for (let year = 1; year <= Math.ceil(totalMonths / 12); year++) {
      let yearlyEMI = 0;
      let yearlyInterest = 0;
      let yearlyPrincipal = 0;
      let yearlyPrepayment = 0;

      for (let month = 1; month <= 12; month++) {
        const currentMonth = (year - 1) * 12 + month;
        if (currentMonth > totalMonths || remainingPrincipal <= 0) break;

        const interestComponent = remainingPrincipal * monthlyRate;
        const principalComponent = emi - interestComponent;

        yearlyEMI += emi;
        yearlyInterest += interestComponent;
        yearlyPrincipal += principalComponent;
        remainingPrincipal -= principalComponent;

        // Apply prepayment
        if (prepaymentEnabled && prepayment > 0) {
          if (
            prepaymentType === 'yearly' &&
            year >= startYear &&
            month === 12
          ) {
            const actualPrepayment = Math.min(prepayment, remainingPrincipal);
            yearlyPrepayment += actualPrepayment;
            remainingPrincipal -= actualPrepayment;
          } else if (
            prepaymentType === 'monthly' &&
            currentMonth >= startMonth
          ) {
            const actualPrepayment = Math.min(prepayment, remainingPrincipal);
            yearlyPrepayment += actualPrepayment;
            remainingPrincipal -= actualPrepayment;
          }
        }
      }

      cumulativeInterest += yearlyInterest;
      cumulativePrincipal += yearlyPrincipal;

      tableData.push({
        year: year,
        emi: yearlyEMI,
        principal: yearlyPrincipal,
        interest: yearlyInterest,
        prepayment: yearlyPrepayment,
        totalPayment: yearlyEMI + yearlyPrepayment,
        cumulativePrincipal: cumulativePrincipal,
        cumulativeInterest: cumulativeInterest,
        outstandingBalance: Math.max(0, remainingPrincipal),
      });

      if (remainingPrincipal <= 0) break;
    }

    return tableData;
  };

  const chartData = generateChartData();
  const tableData = generateTableData();

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          usePointStyle: true,
          padding: 20,
          font: {
            size: 12,
            family: 'Inter, sans-serif',
          },
        },
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: 'white',
        bodyColor: 'white',
        borderColor: COLORS.accent,
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: true,
        callbacks: {
          label: function (context: any) {
            return `${context.dataset.label}: ₹${context.parsed.y.toLocaleString('en-IN')}`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: 11,
            family: 'Inter, sans-serif',
          },
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.1)',
        },
        ticks: {
          font: {
            size: 11,
            family: 'Inter, sans-serif',
          },
          callback: function (value: any) {
            return `₹${value.toLocaleString('en-IN')}`;
          },
        },
      },
    },
    elements: {
      point: {
        hoverRadius: 8,
      },
    },
  };

  return (
    <ToolLayout
      title={`Home loan calculator | ${CONTACT_INFO.company.name}`}
      heroTitle="Home loan calculator"
      description="Calculate your home loan EMI, total interest, and create an amortization schedule with prepayment options"
      canonicalUrl="https://niveshright.com/tools/home-loan-calculator"
      backgroundColor={COLORS.mist}
      heroStyle="gradient"
      heroIcon={
        <div
          className="w-16 h-16 rounded-2xl flex items-center justify-center"
          style={{ backgroundColor: `${COLORS.sage}20` }}
        >
          <Home size={32} style={{ color: COLORS.sage }} />
        </div>
      }
      showBreadcrumb={true}
      keywords="home loan calculator, EMI calculator, mortgage calculator, loan amortization, prepayment calculator, home loan EMI"
      cta={
        <WhatsAppCTA
          title="Need help with home loan planning?"
          description="Get personalized advice on home loans and EMI optimization."
          phoneNumber={CONTACT_INFO.phone.whatsapp}
          message={`Hi ${CONTACT_INFO.contact_person.short_name}! I used your home loan calculator with these details:

LOAN DETAILS:
• Loan amount: ${formatCurrency(loanAmount || 0)}
• Interest rate: ${interestRate || 0}% p.a.
• Loan tenure: ${loanTenure || 0} ${tenureType}${
            prepaymentEnabled && prepaymentAmount
              ? `
Prepayment: ${formatCurrency(prepaymentAmount)} ${prepaymentType}`
              : ''
          }

CALCULATION RESULTS:
• Monthly EMI: ${formatCurrency(results.monthlyEMI)}
• Total interest: ${formatCurrency(results.totalInterest)}
• Total amount: ${formatCurrency(results.totalAmount)}${
            results.interestSaved > 0
              ? `
Interest saved: ${formatCurrency(results.interestSaved)}`
              : ''
          }

I need personalized advice on home loan options and EMI optimization. Can you help me with a strategy to minimize my interest burden?`}
          buttonText="Get home loan advice"
          backgroundColor={COLORS.pearl}
          buttonColor={COLORS.sage}
        />
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        {/* Input Section */}
        <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
          <h2
            className="text-2xl font-bold mb-8"
            style={{ color: COLORS.primary }}
          >
            Loan details
          </h2>
          <NumberInputWithSlider
            label="Loan amount"
            value={loanAmount}
            onChange={setLoanAmount}
            min={100000}
            max={50000000}
            step={100000}
            prefix="₹"
            showTooltip={true}
            minLabel={`₹${formatIndianNumberShort(100000, false)}`}
            maxLabel={`₹${formatIndianNumberShort(50000000, false)}`}
            className="mb-8"
          />

          <NumberInputWithSlider
            label="Interest rate (per annum)"
            value={interestRate}
            onChange={setInterestRate}
            min={6}
            max={15}
            step={0.1}
            minLabel="6%"
            maxLabel="15%"
            className="mb-8"
          />

          <RadioGroup
            label="Tenure type"
            options={[
              { value: 'years', label: 'Years' },
              { value: 'months', label: 'Months' },
            ]}
            value={tenureType}
            onChange={value => setTenureType(value as 'years' | 'months')}
            className="mb-8"
          />

          <NumberInputWithSlider
            label={`Loan tenure (${tenureType})`}
            value={loanTenure}
            onChange={setLoanTenure}
            min={tenureType === 'years' ? 1 : 12}
            max={tenureType === 'years' ? 30 : 360}
            step={1}
            minLabel={tenureType === 'years' ? '1 year' : '12 months'}
            maxLabel={tenureType === 'years' ? '30 years' : '360 months'}
            className="mb-8"
          />

          {/* Prepayment Options */}
          <Toggle
            label="Enable prepayment"
            checked={prepaymentEnabled}
            onChange={setPrepaymentEnabled}
            className="mb-8"
          />

          {prepaymentEnabled && (
            <>
              <RadioGroup
                label="Prepayment frequency"
                options={[
                  { value: 'yearly', label: 'Yearly' },
                  { value: 'monthly', label: 'Monthly' },
                ]}
                value={prepaymentType}
                onChange={value =>
                  setPrepaymentType(value as 'yearly' | 'monthly')
                }
                className="mb-8"
              />

              <NumberInputWithSlider
                label={`${prepaymentType === 'yearly' ? 'Yearly' : 'Monthly'} prepayment amount`}
                value={prepaymentAmount}
                onChange={setPrepaymentAmount}
                min={10000}
                max={1000000}
                step={10000}
                prefix="₹"
                showTooltip={true}
                minLabel={`₹${formatIndianNumberShort(10000, false)}`}
                maxLabel={`₹${formatIndianNumberShort(1000000, false)}`}
                className="mb-8"
              />

              <NumberInputWithSlider
                label="Start prepayment from year"
                value={prepaymentStartYear}
                onChange={setPrepaymentStartYear}
                min={1}
                max={Math.min(30, loanTenure || 30)}
                step={1}
                minLabel="Year 1"
                maxLabel={`Year ${Math.min(30, loanTenure || 30)}`}
                className="mb-0"
              />
            </>
          )}
        </div>

        {/* Results Section */}
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 gap-6">
            <ResultSummaryCard
              title="Monthly EMI"
              value={<LocalizedCurrency value={results.monthlyEMI} />}
              subtitle="Your monthly payment"
              icon={Calculator}
              iconColor={COLORS.accent}
              valueColor={COLORS.accent}
              delay={0.6}
            />

            <ResultSummaryCard
              title="Total interest"
              value={<LocalizedCurrency value={results.totalInterest} />}
              subtitle="Interest over loan tenure"
              icon={Percent}
              iconColor={COLORS.gold}
              valueColor={COLORS.gold}
              delay={0.7}
            />

            <ResultSummaryCard
              title="Total amount"
              value={<LocalizedCurrency value={results.totalAmount} />}
              subtitle="Principal + Interest"
              icon={IndianRupee}
              iconColor={COLORS.sage}
              valueColor={COLORS.sage}
              delay={0.8}
            />

            {prepaymentEnabled && results.interestSaved > 0 && (
              <ResultSummaryCard
                title="Interest saved"
                value={<LocalizedCurrency value={results.interestSaved} />}
                subtitle={`Tenure reduced by ${Math.round(results.tenureReduction / 12)} years ${Math.round(results.tenureReduction % 12)} months`}
                icon={TrendingUp}
                iconColor={COLORS.primary}
                valueColor={COLORS.primary}
                delay={0.9}
              />
            )}
          </div>

          {/* Loan Breakdown Chart */}
          <motion.div
            className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, delay: 0.2 }}
          >
            <div className="flex items-center mb-6">
              <BarChart3
                size={24}
                style={{ color: COLORS.gold }}
                className="mr-3"
              />
              <h3
                className="text-lg font-semibold"
                style={{ color: COLORS.primary }}
              >
                Principal vs interest breakdown (Yearly)
              </h3>
            </div>

            <div className="h-80">
              <Line data={chartData} options={chartOptions} />
            </div>

            {prepaymentEnabled && results.interestSaved > 0 && (
              <div className="mt-4 p-4 bg-gradient-to-r from-green-50 to-blue-50 rounded-xl text-center">
                <p className="text-sm font-medium text-gray-600 mb-1">
                  Interest saved with prepayment
                </p>
                <p
                  className="text-2xl font-bold"
                  style={{ color: COLORS.sage }}
                >
                  <LocalizedCurrency value={results.interestSaved} />
                </p>
                <p className="text-sm text-gray-500">
                  Tenure reduced by {Math.round(results.tenureReduction / 12)}{' '}
                  years {Math.round(results.tenureReduction % 12)} months
                </p>
              </div>
            )}
          </motion.div>
        </div>
      </div>

      {tableData.length > 0 && (
        <motion.div
          className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 mt-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 1.0 }}
        >
          <div className="flex items-center mb-6">
            <FileText
              size={24}
              style={{ color: COLORS.gold }}
              className="mr-3"
            />
            <h3
              className="text-lg font-semibold"
              style={{ color: COLORS.primary }}
            >
              Year-wise amortization schedule
            </h3>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th
                    className="text-left py-3 px-2 font-semibold"
                    style={{ color: COLORS.charcoal }}
                  >
                    Year
                  </th>
                  <th
                    className="text-right py-3 px-2 font-semibold"
                    style={{ color: COLORS.charcoal }}
                  >
                    EMI
                  </th>
                  <th
                    className="text-right py-3 px-2 font-semibold"
                    style={{ color: COLORS.charcoal }}
                  >
                    Principal
                  </th>
                  <th
                    className="text-right py-3 px-2 font-semibold"
                    style={{ color: COLORS.charcoal }}
                  >
                    Interest
                  </th>
                  {prepaymentEnabled && (
                    <th
                      className="text-right py-3 px-2 font-semibold"
                      style={{ color: COLORS.charcoal }}
                    >
                      Prepayment
                    </th>
                  )}
                  <th
                    className="text-right py-3 px-2 font-semibold"
                    style={{ color: COLORS.charcoal }}
                  >
                    Outstanding balance
                  </th>
                </tr>
              </thead>
              <tbody>
                {tableData.map((row, index) => (
                  <tr
                    key={index}
                    className="border-b border-gray-100 hover:bg-gray-50"
                  >
                    <td className="py-3 px-2 font-medium text-gray-900">
                      {row.year}
                    </td>
                    <td className="py-3 px-2 text-right text-gray-700">
                      <LocalizedCurrency value={row.emi} />
                    </td>
                    <td className="py-3 px-2 text-right text-green-600">
                      <LocalizedCurrency value={row.principal} />
                    </td>
                    <td className="py-3 px-2 text-right text-red-600">
                      <LocalizedCurrency value={row.interest} />
                    </td>
                    {prepaymentEnabled && (
                      <td className="py-3 px-2 text-right text-blue-600">
                        <LocalizedCurrency value={row.prepayment} />
                      </td>
                    )}
                    <td className="py-3 px-2 text-right font-medium text-gray-900">
                      <LocalizedCurrency value={row.outstandingBalance} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>
      )}

      <motion.div
        className="mt-16 max-w-5xl mx-auto px-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 1.1 }}
      >
        <article className="prose prose-lg md:prose-xl max-w-none">
          <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-6 md:mb-8">
            Understanding home loan EMI
          </h2>

          <p className="text-lg text-gray-700 leading-relaxed mb-6 md:mb-8">
            <strong>Equated Monthly Installment (EMI)</strong> is like paying
            rent for your own house until you fully own it. It's a fixed amount
            you pay every month that includes both the loan amount (principal)
            and the interest charged by the bank. Think of it as your monthly
            commitment to eventually own your dream home completely.
          </p>

          <h3 className="text-xl md:text-2xl font-semibold text-gray-900 mt-8 md:mt-10 mb-4 md:mb-6">
            An example
          </h3>
          <p className="text-lg text-gray-700 leading-relaxed mb-6 md:mb-8">
            Imagine you want to buy a house worth ₹50 lakh but only have ₹10
            lakh. The bank lends you ₹40 lakh at 8.5% interest for 20 years.
            Your EMI of ₹34,713 includes both repaying the ₹40 lakh loan and
            paying interest to the bank. It's like paying monthly rent, but each
            payment brings you closer to full ownership!
          </p>

          <h3 className="text-xl md:text-2xl font-semibold text-gray-900 mt-8 md:mt-10 mb-4 md:mb-6">
            How EMI changes over time
          </h3>
          <p className="text-lg text-gray-700 leading-relaxed mb-6 md:mb-8">
            In early years, most of your EMI goes toward interest (like paying
            rent to the bank). As years pass, more of your EMI goes toward the
            actual loan amount (principal). By the end, you're mostly paying off
            the loan itself. This is called the amortization schedule.
          </p>

          <h3 className="text-xl md:text-2xl font-semibold text-gray-900 mt-8 md:mt-10 mb-4 md:mb-6">
            Important terms
          </h3>
          <div className="space-y-4 md:space-y-5 mb-8 md:mb-10">
            <p className="text-lg text-gray-700">
              <strong>Principal:</strong> The actual loan amount you borrowed
              from the bank
            </p>
            <p className="text-lg text-gray-700">
              <strong>Interest rate:</strong> The cost of borrowing money - like
              rent you pay to use the bank's money
            </p>
            <p className="text-lg text-gray-700">
              <strong>Tenure:</strong> How long you take to repay the loan -
              longer tenure means smaller EMI but more total interest
            </p>
            <p className="text-lg text-gray-700">
              <strong>Prepayment:</strong> Paying extra money toward your loan
              to finish it faster and save interest
            </p>
            <p className="text-lg text-gray-700">
              <strong>Outstanding balance:</strong> How much loan amount you
              still owe to the bank
            </p>
            <p className="text-lg text-gray-700">
              <strong>Amortization:</strong> The schedule showing how your EMI
              is split between principal and interest each year
            </p>
          </div>

          <h3 className="text-xl md:text-2xl font-semibold text-gray-900 mt-8 md:mt-10 mb-4 md:mb-6">
            Benefits of prepayment
          </h3>

          <h4 className="text-lg md:text-xl font-semibold text-gray-900 mt-6 md:mt-8 mb-3 md:mb-4">
            Interest savings
          </h4>
          <p className="text-lg text-gray-700 mb-4 md:mb-6">
            Every extra rupee you pay toward your loan reduces the outstanding
            balance, which means less interest in future EMIs. It's like paying
            off a high-interest credit card - the sooner you pay, the less
            interest you pay overall.
          </p>

          <h4 className="text-lg md:text-xl font-semibold text-gray-900 mt-6 md:mt-8 mb-3 md:mb-4">
            Faster ownership
          </h4>
          <p className="text-lg text-gray-700 mb-4 md:mb-6">
            Prepayments help you become debt-free faster. Instead of a 20-year
            loan, you might finish in 15 years, giving you complete ownership of
            your home sooner and peace of mind.
          </p>

          <h4 className="text-lg md:text-xl font-semibold text-gray-900 mt-6 md:mt-8 mb-3 md:mb-4">
            When to prepay
          </h4>
          <ul className="text-lg text-gray-700 mb-4 md:mb-6 list-disc ml-12">
            <li className="py-1">
              <strong>Early years:</strong> Maximum benefit as most of your EMI
              is interest. Prepaying now saves the most money.
            </li>
            <li className="py-1">
              <strong>Bonus/windfall:</strong> Use salary bonuses, tax refunds,
              or inheritance to make lump sum prepayments.
            </li>
            <li className="py-1">
              <strong>Low investment options:</strong> If you can't find
              investments giving higher returns than your loan interest rate.
            </li>
          </ul>

          <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-4">
            Home loan vs other borrowing options
          </h3>
          <div className="overflow-x-auto mb-6">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">
                    Borrowing type
                  </th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-900">
                    Interest rate
                  </th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">
                    Characteristics
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-gray-100">
                  <td className="py-3 px-4 font-medium text-gray-900">
                    Home loan
                  </td>
                  <td className="py-3 px-4 text-center text-gray-700">8-9%</td>
                  <td className="py-3 px-4 text-gray-700">
                    Lowest rates, tax benefits, long tenure
                  </td>
                </tr>
                <tr className="border-b border-gray-100">
                  <td className="py-3 px-4 font-medium text-gray-900">
                    Personal loan
                  </td>
                  <td className="py-3 px-4 text-center text-gray-700">
                    12-18%
                  </td>
                  <td className="py-3 px-4 text-gray-700">
                    Higher rates, no collateral needed
                  </td>
                </tr>
                <tr>
                  <td className="py-3 px-4 font-medium text-gray-900">
                    Credit card
                  </td>
                  <td className="py-3 px-4 text-center text-gray-700">
                    24-48%
                  </td>
                  <td className="py-3 px-4 text-gray-700">
                    Highest rates, avoid for large purchases
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-4">
            Tax benefits on home loans
          </h3>
          <div className="space-y-4 md:space-y-5 mb-8 md:mb-10">
            <p className="text-lg text-gray-700">
              <strong>Section 80C:</strong> Deduction up to ₹1.5 lakh on
              principal repayment
            </p>
            <p className="text-lg text-gray-700">
              <strong>Section 24(b):</strong> Deduction up to ₹2 lakh on
              interest paid (self-occupied property)
            </p>
            <p className="text-lg text-gray-700">
              <strong>First-time buyer:</strong> Additional ₹50,000 deduction
              under Section 80EE for eligible buyers
            </p>
          </div>

          <h3 className="text-xl md:text-2xl font-semibold text-gray-900 mt-8 md:mt-10 mb-4 md:mb-6">
            What is home loan refinancing?
          </h3>
          <p className="text-lg text-gray-700 leading-relaxed mb-6 md:mb-8">
            <strong>Refinancing</strong> means switching your existing home loan
            from one bank to another bank that offers better terms. It's like
            changing your mobile service provider for a better plan - you keep
            the same phone (house) but get better rates and conditions.
          </p>

          <h4 className="text-lg md:text-xl font-semibold text-gray-900 mt-6 md:mt-8 mb-3 md:mb-4">
            When to consider refinancing
          </h4>
          <ul className="text-lg text-gray-700 mb-4 md:mb-6 list-disc ml-12">
            <li className="py-1">
              <strong>Interest rate drop:</strong> If market rates have fallen
              by 0.5-1% or more since you took your loan
            </li>
            <li className="py-1">
              <strong>Better service:</strong> Your current bank has poor
              customer service or slow processing
            </li>
            <li className="py-1">
              <strong>Additional features:</strong> New bank offers better
              features like free prepayment, lower processing fees
            </li>
            <li className="py-1">
              <strong>Loan amount:</strong> You have a large outstanding amount
              (₹20+ lakh) where even small rate differences matter
            </li>
          </ul>

          <h4 className="text-lg md:text-xl font-semibold text-gray-900 mt-6 md:mt-8 mb-3 md:mb-4">
            Refinancing process
          </h4>
          <div className="space-y-4 md:space-y-5 mb-6 md:mb-8">
            <p className="text-lg text-gray-700">
              <strong>Step 1:</strong> Compare offers from different banks -
              check interest rates, processing fees, and terms
            </p>
            <p className="text-lg text-gray-700">
              <strong>Step 2:</strong> Apply to the new bank with required
              documents (same as original home loan process)
            </p>
            <p className="text-lg text-gray-700">
              <strong>Step 3:</strong> New bank pays off your existing loan and
              takes over the property documents
            </p>
            <p className="text-lg text-gray-700">
              <strong>Step 4:</strong> Start paying EMI to the new bank at the
              new (hopefully lower) interest rate
            </p>
          </div>

          <h4 className="text-lg md:text-xl font-semibold text-gray-900 mt-6 md:mt-8 mb-3 md:mb-4">
            Costs involved in refinancing
          </h4>
          <div className="overflow-x-auto mb-6">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">
                    Cost type
                  </th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-900">
                    Typical amount
                  </th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">
                    Description
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-gray-100">
                  <td className="py-3 px-4 font-medium text-gray-900">
                    Processing fee (new bank)
                  </td>
                  <td className="py-3 px-4 text-center text-gray-700">
                    0.5-1% of loan
                  </td>
                  <td className="py-3 px-4 text-gray-700">
                    Fee charged by new bank for processing your application
                  </td>
                </tr>
                <tr className="border-b border-gray-100">
                  <td className="py-3 px-4 font-medium text-gray-900">
                    Prepayment penalty (old bank)
                  </td>
                  <td className="py-3 px-4 text-center text-gray-700">
                    0-2% of outstanding
                  </td>
                  <td className="py-3 px-4 text-gray-700">
                    Penalty for closing loan early (many banks have removed
                    this)
                  </td>
                </tr>
                <tr className="border-b border-gray-100">
                  <td className="py-3 px-4 font-medium text-gray-900">
                    Legal & documentation
                  </td>
                  <td className="py-3 px-4 text-center text-gray-700">
                    ₹10,000-25,000
                  </td>
                  <td className="py-3 px-4 text-gray-700">
                    Lawyer fees, stamp duty, registration charges
                  </td>
                </tr>
                <tr>
                  <td className="py-3 px-4 font-medium text-gray-900">
                    Property valuation
                  </td>
                  <td className="py-3 px-4 text-center text-gray-700">
                    ₹2,000-5,000
                  </td>
                  <td className="py-3 px-4 text-gray-700">
                    New bank's property assessment charges
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <h4 className="text-lg md:text-xl font-semibold text-gray-900 mt-6 md:mt-8 mb-3 md:mb-4">
            Is refinancing worth it?
          </h4>
          <p className="text-lg text-gray-700 mb-4 md:mb-6">
            <strong>Simple rule:</strong> If the "interest rate difference ×
            remaining loan amount × remaining years" is greater than the total
            refinancing costs, then refinancing makes financial sense.
          </p>

          <h4 className="text-lg md:text-xl font-semibold text-gray-900 mt-6 md:mt-8 mb-3 md:mb-4">
            Refinancing example
          </h4>
          <p className="text-lg text-gray-700 mb-4 md:mb-6">
            Let's say you have ₹30 lakh outstanding at 9% interest with 15 years
            remaining. A new bank offers the same amount at 8% interest. Your
            total interest savings would be ₹1,65,000 over 15 years. Even after
            paying ₹50,000 in refinancing costs, you save ₹1,15,000 - making
            refinancing a smart financial decision.
          </p>

          <div className="mt-8 p-6 bg-blue-50 rounded-xl border border-blue-200">
            <h4 className="text-lg font-semibold text-gray-900 mb-3">
              Pro tip!
            </h4>
            <p className="text-gray-700">
              Start with a comfortable EMI (30-40% of income), make prepayments
              in early years when possible, and consider refinancing if rates
              drop significantly. Remember, your home is both a place to live
              and a long-term investment that typically appreciates over time.
            </p>
          </div>
        </article>
      </motion.div>
    </ToolLayout>
  );
};
